import {MbscCalendarColor, MbscCalendarEvent} from "@mobiscroll/react";
import {AvailabilityType} from "crqlar-react-common/dist/models/availability/availability.model";
import {AvailabilityCalculationResult} from "crqlar-react-common/dist/models/availability/availabilityCalculationResult.model";
import {AvailabilityService} from "crqlar-react-common/dist/services/availability/availability.service";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import type {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {useCallback, useMemo} from "react";

interface UseCalendarAvailabilityEventsParams {
    availabilityResults: AvailabilityCalculationResult[];
    refetchData: () => Promise<void>;
    // Child resources (e.g. restaurant tables) — the parent's block is mirrored onto them
    childResources?: Resource[];
}

export function useCalendarAvailabilityEvents({
    availabilityResults,
    refetchData,
    childResources = [],
}: UseCalendarAvailabilityEventsParams) {
    const availabilityColors = useMemo<MbscCalendarColor[]>(() => (
        availabilityResults.map(result => ({
            start: result.startTime,
            end: result.endTime,
            background: result.color === CommonThemeColors.sys.availability.boldRed
                ? CommonThemeColors.sys.availability.lightRed
                : CommonThemeColors.sys.availability.lightBlue,
            resource: result.entityId,
            title: result.name,
            cssClass: "availability-block",
        }))
    ), [availabilityResults]);

    // Restaurant tables are child resources without availability of their own: the parent's
    // (restaurant's) availability applies to every table under it.
    const childIdsByParent = useMemo(() => {
        const map = new Map<string, string[]>();
        childResources.forEach(child => {
            if (!child.parentId || !child.id) return;
            const ids = map.get(child.parentId) ?? [];
            ids.push(child.id);
            map.set(child.parentId, ids);
        });
        return map;
    }, [childResources]);

    const parentIdByChild = useMemo(() => {
        const map = new Map<string, string>();
        childIdsByParent.forEach((childIds, parentId) => childIds.forEach(childId => map.set(childId, parentId)));
        return map;
    }, [childIdsByParent]);

    // Display-only: the restaurant's availability applies to all its tables, so paint the same
    // background on every child row — red when the restaurant is closed, blue when it is open.
    // Kept separate from availabilityColors, which drives block-overlap validation — children stay
    // untouched there, so system behaviour does not change.
    const availabilityDisplayColors = useMemo<MbscCalendarColor[]>(() => {
        if (!childIdsByParent.size) return availabilityColors;

        const childColors = availabilityColors
            .flatMap(color => (childIdsByParent.get(String(color.resource ?? "")) ?? []).map(childId => ({
                start: color.start,
                end: color.end,
                background: color.background,
                resource: childId,
                cssClass: "availability-block availability-block--inherited",
            })));

        return childColors.length ? [...availabilityColors, ...childColors] : availabilityColors;
    }, [availabilityColors, childIdsByParent]);

    const blockPeriodEvents = useMemo<MbscCalendarEvent[]>(() => {
        const closedResults = availabilityResults.filter(result => result.type === AvailabilityType.CLOSED);

        const ownEvents = closedResults.map(result => ({
            start: result.startTime,
            end: result.endTime,
            resource: result.entityId,
            title: result.name || "",
            id: `block-${result.availabilityId}-${new Date(result.startTime!).getTime()}`,
            isBlockPeriod: true,
            availabilityId: result.availabilityId,
            blockCalcResult: result,
            cssClass: "block-period-event",
            // A block from a recurring availability (e.g. a staff member's day off on her working-hours
            // series) is one occurrence of that series: dragging it would rewrite the whole series.
            editable: !result.rrule,
        }));

        // The restaurant's block, repeated on each of its tables. It is the same block period:
        // clicking, moving or resizing it on any table edits the restaurant's availability
        // (blockCalcResult / availabilityId / parentResourceId all point at the restaurant).
        // It can't be moved onto another row — a table has no block period of its own.
        const inheritedEvents = closedResults.flatMap(result =>
            (childIdsByParent.get(result.entityId ?? "") ?? []).map(childId => ({
                start: result.startTime,
                end: result.endTime,
                resource: childId,
                title: "",
                id: `block-${result.availabilityId}-${new Date(result.startTime!).getTime()}-${childId}`,
                isBlockPeriod: true,
                isInheritedBlockPeriod: true,
                parentResourceId: result.entityId,
                availabilityId: result.availabilityId,
                blockCalcResult: result,
                cssClass: "block-period-event block-period-event--inherited",
                editable: !result.rrule,
                dragBetweenResources: false,
            }))
        );

        return inheritedEvents.length ? [...ownEvents, ...inheritedEvents] : ownEvents;
    }, [availabilityResults, childIdsByParent]);

    const handleBlockPeriodUpdated = useCallback(async (
        availabilityId: string,
        newStart: Date,
        newEnd: Date,
        newResourceId?: string
    ) => {
        try {
            const availability = await AvailabilityService.getAvailabilityById(availabilityId);
            // The dropped times belong to one occurrence. Writing them onto a recurring series would
            // move its start to that day and orphan its exceptions, so recurring blocks are never saved here.
            if (availability.rrule) {
                await refetchData();
                return;
            }
            // A block dropped on a table belongs to that table's restaurant — tables never
            // carry availability of their own
            const targetResourceId = newResourceId
                ? parentIdByChild.get(newResourceId) ?? newResourceId
                : undefined;
            await AvailabilityService.updateAvailability({
                ...availability,
                startTime: newStart,
                endTime: newEnd,
                entityId: targetResourceId || availability.entityId,
            });
            await refetchData();
        } catch (error) {
            console.error("Failed to update block period", error);
            await refetchData();
        }
    }, [parentIdByChild, refetchData]);

    return {
        availabilityColors,
        availabilityDisplayColors,
        blockPeriodEvents,
        handleBlockPeriodUpdated,
    };
}
